import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import { CONTACT_PHONE, CONTACT_EMAIL } from '../../utils/constants';

const CALENDLY_URL = 'https://calendly.com/northbridgevaluation-info/15min';

const BookConsultation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    // Listen for Calendly booking confirmation event
    const handleCalendlyEvent = (e) => {
      if (e.data?.event === 'calendly.event_scheduled') {
        navigate('/booking-confirmation');
      }
    };
    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      document.body.removeChild(script);
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Book a Free Consultation | Northbridge Valuation & Advisory</title>
        <meta
          name="description"
          content="Schedule a free 15-minute consultation with our CVA & CPA-certified valuation experts. Choose a time that works for you."
        />
        <link
          href="https://assets.calendly.com/assets/external/widget.css"
          rel="stylesheet"
        />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-surface border-b border-border py-12 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-2 mb-6">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-accent">CVA & CPA Certified Experts</span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Schedule Your Free Consultation
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Pick a time that works for you. Our certified valuation professionals will discuss your
              needs and how we can deliver a defensible, investor-grade valuation.
            </p>

            {/* Trust markers */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="Clock" size={16} className="text-accent" />
                <span className="text-sm">15-Minute Call</span>
              </div>
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="Shield" size={16} className="text-accent" />
                <span className="text-sm">No Obligation</span>
              </div>
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="Award" size={16} className="text-accent" />
                <span className="text-sm">CVA & CPA Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="TrendingUp" size={16} className="text-accent" />
                <span className="text-sm">30+ Years Combined Experience</span>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly Embed Section */}
        <section className="py-8 px-4">
          <div className="max-w-5xl mx-auto">
            <div
              className="calendly-inline-widget rounded-xl overflow-hidden border border-border shadow-brand"
              data-url={CALENDLY_URL}
              style={{ minWidth: '320px', height: '700px' }}
            />
          </div>
        </section>

        {/* Alternative Contact */}
        <section className="py-10 px-6 border-t border-border bg-surface">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-text-secondary mb-4 text-sm">
              Prefer to reach out directly?
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={`tel:${CONTACT_PHONE}`}
                className="flex items-center justify-center space-x-2 text-text-primary hover:text-accent transition-colors duration-200"
              >
                <Icon name="Phone" size={16} className="text-accent" />
                <span className="text-sm font-medium">{CONTACT_PHONE}</span>
              </a>
              <span className="hidden sm:block text-border">|</span>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center justify-center space-x-2 text-text-primary hover:text-accent transition-colors duration-200"
              >
                <Icon name="Mail" size={16} className="text-accent" />
                <span className="text-sm font-medium">{CONTACT_EMAIL}</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BookConsultation;
